<template>
	<nav v-click-outside="hideMenu" class="main" :class="{ active: showMenu }">
		<div class="row">
			<div class="columns column12">
				<nuxt-link
					class="logo"
					:class="page.headingImage ? '' : 'black'"
					aria-label="Back to homepage"
					:to="localePath('/')"
				/>
				<div class="nav-container">
					<ul class="main-nav">
						<li
							v-for="item in topmenu[locale]"
							:key="item.filename"
							:class="{ 'has-dropdown': item.submenu.length }"
						>
							<nuxt-link :exact="item.slug === 'index'" :to="`${item.filename}`" @click="hideMenu">
								{{ item.header }}
							</nuxt-link>
							<submenu
								v-if="item.submenu.length"
								:hide-all="!showMenu"
								:item="item"
								:subitems="item.submenu"
								@hide-menu="hideMenu"
							/>
						</li>
					</ul>
					<div class="contact-info">
						<p>
							<strong>{{ defaults[locale].website.hotelName }}</strong>
							<br />
							<span>{{ defaults[locale].website.street }} {{ defaults[locale].website.number }}</span>
							<br />
							<span>{{ defaults[locale].website.postalCode }} {{ defaults[locale].website.city }}</span>
							<br />
							<span>{{ defaults[locale].website.country }}</span>
							<br /><br />
							<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
								<font-awesome-icon icon="fa-light fa-phone" size="1x" />
								<span>{{ defaults[locale].website.phone }}</span>
							</a>
							<br />
							<a :href="`mailto:${defaults[locale].website.email}`">
								<font-awesome-icon icon="fa-light fa-envelope" size="1x" />
								<span>{{ defaults[locale].website.email }}</span>
							</a>
						</p>
						<social-media :socials="socials" />
					</div>
					<ul v-if="page" class="lang-nav">
						<li v-for="item in page.langmenu" :key="`${item.filename}-${item.language}`">
							<nuxt-link class="flag" :class="item.language" :to="`${item.filename}`" @click="hideMenu">
								{{ $t(item.language.toLowerCase()) }}
							</nuxt-link>
						</li>
					</ul>
				</div>
				<div class="buttons">
					<book-button class="button button-cta reserve-now right">
						<span>{{ $t('bookNow') }}</span>
					</book-button>
					<a class="mobile-menu-button" :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
						<font-awesome-icon icon="fa-light fa-phone" size="1x" />
					</a>
					<div class="menu-button button right button-en" :class="{ active: showMenu }" @click="toggleMenu">
						<font-awesome-icon v-if="showMenu" icon="fa-light fa-times" size="1x" />
						<div class="menu-button-icons">
							<div class="menu-bar" />
							<div class="menu-bar" />
							<div class="menu-bar" />
						</div>
						<span>{{ $t('menu') }}</span>
					</div>
				</div>
			</div>
		</div>
	</nav>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const showMenu = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
};

const hideMenu = () => {
	showMenu.value = false;
};
</script>

<style lang="scss" scoped>
nav {
	width: 100%;
	padding: 15px 0;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	transition: all 0.3s ease-in-out;

	.row {
		overflow: visible;
	}
}

.logo {
	background: url('~/assets/images/logo-hotel-velotel-big-white.png') no-repeat center center;
	background-size: 200px;
	width: 200px;
	height: 59px;
	display: block;
	transition: all 0.3s ease-in-out;

	&.black {
		background: url('~/assets/images/logo-hotel-velotel-big-black.png') no-repeat center center;
		background-size: 200px;
	}
}

.flag::before {
	content: '';
	width: 19px;
	position: relative;
	top: 4px;
	height: 19px;
	display: inline-block;
	margin-right: 7px;
}

.flag.en::before {
	background: url('~/assets/images/en.png') no-repeat;
	background-size: 19px 19px;
}

.flag.nl::before {
	background: url('~/assets/images/nl.png') no-repeat;
	background-size: 19px 19px;
}

.flag.de::before {
	background: url('~/assets/images/de.png') no-repeat;
	background-size: 19px 19px;
}

.flag.fr::before {
	background: url('~/assets/images/fr.png') no-repeat;
	background-size: 19px 19px;
}

.flag.es::before {
	background: url('~/assets/images/es.png') no-repeat;
	background-size: 19px 19px;
}

.main-nav {
	list-style: none;
	text-align: left;
	font-weight: 500;
	line-height: 26px;
	width: 55%;
	font-size: 22px;
	float: left;
	visibility: visible;

	& > li {
		display: block;
		float: left;
		margin: 6px 0;
		width: 100%;
	}

	a {
		color: $body-color;
		padding: 6px 0;
		font-weight: 300;
		text-decoration: none;
		transition: color 0.3s ease-in-out;

		&.active,
		&:hover {
			color: $cta-color;
		}
	}
}

.contact-info {
	float: left;
	width: 45%;

	svg {
		color: $cta-color;
		margin: 0 4px 0 0;
	}
}

.lang-nav {
	font-size: 16px;
	list-style: none;
	text-align: left;
	float: left;
	width: 50%;
	line-height: 26px;
	padding-top: 40px;

	li {
		margin: 3px 0;
	}

	a {
		color: $body-color;

		&.router-link-exact-active,
		&:hover {
			color: $cta-color;
		}
	}

	img {
		max-width: 19px;
		float: left;
		margin: 3px 8px 0 0;
	}
}

.nav-container {
	font-size: 18px;
	position: fixed;
	top: 0;
	right: -105vw;
	bottom: 0;
	background: #fff;
	min-width: 36%;
	width: 700px;
	max-width: 100%;
	padding: 150px 50px 20px;
	z-index: 100;
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);
	transition: right 0.3s ease-in-out;
}

.active .nav-container {
	right: 0;
	transition: right 0.5s ease-in-out;
}

.nav-overlay {
	display: none;
	position: fixed;
	inset: 0;
	background: rgb(0 0 0 / 30%);
	z-index: 99;
	opacity: 0;
	transition: opacity 0.3s ease-in-out;

	&.active {
		display: block;
		opacity: 1;
	}
}

.mobile-menu-button {
	display: none;
	color: #333;
	height: 43px;
	width: 54px;
	text-align: center;
	background: #fff;
	border: 1px solid #000;
}

nav .buttons {
	position: fixed;
	top: 15px;
	right: 20px;
	z-index: 101;

	.button {
		margin-top: 4px;
		margin-left: 6px;
		padding: 13px 26px;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			margin-right: 10px;
		}

		&.reserve-now {
			padding: 13px 36px;
		}

		&.menu-button {
			svg {
				width: 35px;
			}
		}
	}

	.menu-button {
		cursor: pointer;
		background: #fff;
		color: #000;
		border: 1px solid #000;

		.menu-button-icons {
			width: 35px;
			margin: 0 10px 0 0;

			div {
				margin: 6px 5px;
				width: 25px;
				height: 1px;
				background: #333;
				transition: all 0.3s ease-in-out;

				&:first-child {
					margin: 0 0 6px 10px;
				}

				&:last-child {
					margin: 6px 10px 0 0;
				}
			}
		}

		&:hover {
			color: #000;

			.menu-button-icons {
				div {
					margin: 4px;

					&:first-child {
						margin: 0 4px 4px;
					}

					&:last-child {
						margin: 4px 0 0 4px;
					}
				}
			}
		}

		&.active {
			color: #000;
			border-color: #000;

			.menu-button-icons {
				display: none;
			}

			i::before {
				content: '\f00d';
				font-weight: 300;
				color: #000;
			}
		}
	}
}

@media (max-height: 740px) {
	.nav-container {
		overflow-y: scroll;
	}

	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		z-index: 1;
	}

	.main-nav {
		z-index: 2;
		position: relative;
	}
}

@media (max-width: 800px) {
	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		float: left;
		margin-top: 30px;
	}
}

@media (max-width: 600px) {
	.nav-container {
		padding: 100px 25px 25px;
	}

	.main-nav,
	.contact-info,
	.lang-nav {
		width: 100%;
	}

	.contact-info {
		display: none;
	}

	.lang-nav {
		margin-top: 0;
	}

	nav {
		.column12 {
			padding: 0;
		}

		.mobile-menu-button {
			display: flex;
			flex-flow: row wrap;
			justify-content: center;
			align-items: center;
		}

		.buttons {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			margin: 8px 0 0;

			.button {
				&.reserve-now {
					display: none;
				}

				&.menu-button {
					padding: 13px 10px;
					margin: 0 0 0 5px;

					span {
						display: none;
					}

					svg {
						margin: 0;
					}

					.menu-button-icons {
						margin: 0;
						height: 15px;

						div {
							margin: 5px;

							&:first-child {
								margin: 0 5px 5px;
							}

							&:last-child {
								margin: 5px 5px 0;
							}
						}
					}
				}
			}
		}
	}

	.logo {
		background: url('~/assets/images/logo-hotel-velotel-medium-white.png') no-repeat center center;
		background-size: 170px;
		width: 170px;
		height: 50px;

		&.black {
			background: url('~/assets/images/logo-hotel-velotel-medium-black.png') no-repeat center center;
			background-size: 170px;
		}
	}
}

@media (max-width: 400px) {
	.logo {
		background: url('~/assets/images/logo-hotel-velotel-small-white.png') no-repeat center center;
		background-size: 150px;
		width: 150px;
		height: 44px;

		&.black {
			background: url('~/assets/images/logo-hotel-velotel-small-black.png') no-repeat center center;
			background-size: 150px;
		}
	}
}
</style>
